'use client';

import type { SignInMethod } from '@/models/analytics/features/user';
import type { DesktopAuthErrorContent, DesktopBrowserName } from '@/tauri/auth0';
import { create, type StateCreator } from 'zustand';

type ManualControlAction = 'copy-link' | 'open-browser' | 'fallback';

export interface LoginIntent {
  screenHint: 'signin' | 'signup';
  signInMethod?: SignInMethod;
}

export interface ManualControlState {
  attemptId: string | null;
  authUrl: string | null;
  launchedAt: number | null;
  copyCount: number;
  openAgainCount: number;
  fallbackCount: number;
  lastAction: ManualControlAction | null;
  lastUpdatedAt: number | null;
  browserName: DesktopBrowserName | null;
}

export interface DesktopAuthErrorState extends DesktopAuthErrorContent {
  correlationId?: string | null;
  attemptId?: string | null;
  detail?: string | null;
  lastUpdatedAt: number;
}

export interface DesktopAuthSlice {
  manualControls: ManualControlState;
  authError: DesktopAuthErrorState | null;
  loginIntent: LoginIntent | null;
  /**
   * Epoch ms when the current desktop sign-in attempt launched; null when no
   * attempt is in flight (MUL-3346). Set synchronously by
   * `launchDesktopAuthLogin`; cleared on login success, auth error
   * (`setAuthError` clears it in the same update), launch failure, or the
   * watchdog in `useDesktopLoginPending`. Clearing never cancels the
   * underlying attempt — a late `auth:login-success` still signs the user in.
   */
  loginPendingSince: number | null;
  /**
   * Monotonic counter, bumped every time a pending attempt is resolved
   * (watchdog expiry, login success, launch failure, `setAuthError`).
   *
   * Exists because `useDesktopLoginPending` cannot detect a settle by watching
   * `loginPendingSince` alone: a settle that lands in the same React batch as
   * the launch (a fast `auth:error`, typically the cancelled attempt of a rapid
   * retry) takes the flag non-null → null with no committed render in between,
   * so the rising edge is never observed and the falling-edge release never
   * fires — stranding every sign-in button's spinner permanently. The counter
   * advances regardless of rendering, so a consumer can tell "an attempt
   * resolved since I last looked" without having witnessed it pending.
   */
  loginSettleEpoch: number;
  /**
   * True while a Kinde→Auth0 Apple migration login is in flight (APP-6026).
   *
   * The deep-link callback carries no migration context (only `code`/`state`),
   * so this FE-side marker is the signal `handleLoginSuccess` uses to decide it
   * must revoke the old Kinde session (and order the revoke *before* the Redux
   * token swap). Set right before launching the migration login from
   * `KindeMigrationModal`; read + cleared on `auth:login-success`, and also
   * cleared on `auth:error` so a failed/cancelled migration never leaves the
   * marker set to mis-trigger a revoke on a later normal login.
   */
  migrationInProgress: boolean;
  setManualAttempt: (
    attemptId: string | null,
    authUrl?: string | null,
    browserName?: DesktopBrowserName | null
  ) => void;
  trackCopyLink: () => void;
  trackOpenBrowser: () => void;
  trackFallbackSelection: () => void;
  resetManualControls: () => void;
  setAuthError: (
    error: DesktopAuthErrorContent,
    meta?: {
      correlationId?: string | null;
      attemptId?: string | null;
      detail?: string | null;
    }
  ) => void;
  clearAuthError: () => void;
  setLoginIntent: (intent: LoginIntent) => void;
  clearLoginIntent: () => void;
  beginLoginPending: () => void;
  clearLoginPending: () => void;
  setMigrationInProgress: () => void;
  clearMigrationInProgress: () => void;
}

export type DesktopAuthStore = DesktopAuthSlice;

const defaultManualControls: ManualControlState = {
  attemptId: null,
  authUrl: null,
  launchedAt: null,
  copyCount: 0,
  openAgainCount: 0,
  fallbackCount: 0,
  lastAction: null,
  lastUpdatedAt: null,
  browserName: null,
};

const touchState = (state: ManualControlState, action: ManualControlAction) => ({
  ...state,
  lastAction: action,
  lastUpdatedAt: Date.now(),
});

export const createDesktopAuthSlice: StateCreator<
  DesktopAuthStore,
  [],
  [],
  DesktopAuthSlice
> = (set) => ({
  manualControls: defaultImmerManualState(),
  authError: null,
  loginIntent: null,
  loginPendingSince: null,
  loginSettleEpoch: 0,
  migrationInProgress: false,

  setManualAttempt: (attemptId, authUrl = null, browserName = null) =>
    set((state) => ({
      manualControls: {
        ...state.manualControls,
        attemptId,
        authUrl,
        launchedAt: Date.now(),
        lastUpdatedAt: Date.now(),
        browserName,
      },
      authError: null,
    })),

  trackCopyLink: () =>
    set((state) => ({
      manualControls: {
        ...touchState(state.manualControls, 'copy-link'),
        copyCount: state.manualControls.copyCount + 1,
      },
    })),

  trackOpenBrowser: () =>
    set((state) => ({
      manualControls: {
        ...touchState(state.manualControls, 'open-browser'),
        openAgainCount: state.manualControls.openAgainCount + 1,
      },
    })),

  trackFallbackSelection: () =>
    set((state) => ({
      manualControls: {
        ...touchState(state.manualControls, 'fallback'),
        fallbackCount: state.manualControls.fallbackCount + 1,
      },
    })),

  resetManualControls: () =>
    set(() => ({
      manualControls: defaultImmerManualState(),
    })),

  setAuthError: (error, meta) =>
    set((state) => ({
      authError: {
        ...error,
        correlationId: meta?.correlationId ?? null,
        attemptId: meta?.attemptId ?? null,
        detail: meta?.detail ?? null,
        lastUpdatedAt: Date.now(),
      },
      // An auth:error resolves the in-flight attempt in the same update so
      // sign-in buttons can never observe error-set-but-still-pending.
      loginPendingSince: null,
      loginSettleEpoch:
        state.loginPendingSince === null
          ? state.loginSettleEpoch
          : state.loginSettleEpoch + 1,
      manualControls: {
        ...state.manualControls,
        lastUpdatedAt: Date.now(),
      },
    })),

  clearAuthError: () =>
    set((state) => ({
      authError: null,
      manualControls: {
        ...state.manualControls,
        lastUpdatedAt: Date.now(),
      },
    })),

  setLoginIntent: (intent) =>
    set(() => ({
      loginIntent: intent,
    })),

  clearLoginIntent: () =>
    set(() => ({
      loginIntent: null,
    })),

  beginLoginPending: () =>
    set(() => ({
      loginPendingSince: Date.now(),
    })),

  clearLoginPending: () =>
    set((state) => ({
      loginPendingSince: null,
      loginSettleEpoch:
        state.loginPendingSince === null
          ? state.loginSettleEpoch
          : state.loginSettleEpoch + 1,
    })),

  setMigrationInProgress: () =>
    set(() => ({
      migrationInProgress: true,
    })),

  clearMigrationInProgress: () =>
    set(() => ({
      migrationInProgress: false,
    })),
});

function defaultImmerManualState(): ManualControlState {
  return {
    ...defaultManualControls,
    lastUpdatedAt: Date.now(),
    browserName: null,
  };
}

export const useDesktopAuthStore = create<DesktopAuthStore>()(createDesktopAuthSlice);
